import { useEffect } from 'react';

/**
 * Dynamically updates the browser favicon (and apple-touch-icon) when
 * the app logo changes. This makes the tab icon and home-screen shortcut
 * icon (for recently-added PWAs) reflect the uploaded logo immediately.
 *
 * Note: For *already-installed* PWAs the OS caches the icon from the
 * manifest at install time. The user would need to re-install the PWA for
 * the manifest icon to update. However, the in-browser tab icon updates
 * instantly for everyone.
 */
export function useDynamicFavicon(logoUrl: string | undefined) {
  useEffect(() => {
    // ── Favicon (tab icon) ──────────────────────────────────────────────
    let faviconEl = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!faviconEl) {
      faviconEl = document.createElement('link');
      faviconEl.rel = 'icon';
      document.head.appendChild(faviconEl);
    }

    // ── Apple touch icon ────────────────────────────────────────────────
    let appleIconEl = document.querySelector<HTMLLinkElement>("link[rel='apple-touch-icon']");
    if (!appleIconEl) {
      appleIconEl = document.createElement('link');
      appleIconEl.rel = 'apple-touch-icon';
      document.head.appendChild(appleIconEl);
    }

    if (logoUrl) {
      // Use the uploaded base64 logo
      faviconEl.href = logoUrl;
      appleIconEl.href = logoUrl;
    } else {
      // Restore the static default icons
      faviconEl.href = '/icons/icon-192.png';
      appleIconEl.href = '/icons/icon-192.png';
    }
  }, [logoUrl]);
}
